import React, { Fragment } from 'react';
import styled, { keyframes } from 'styled-components';

import { evaluateProp } from '../utils';
import FjIcon from '../fj-icon';
import { modalDimensions } from './constants';
import type { ModalSize } from './constants';
import FjButton, { fjButtonTypes } from '../fj-button';

interface Dimensions {
  width?: number | string;
  height?: number | string;
}

// Content Wrapper
export const ContentWrapper = styled.div<{ draggable?: boolean; allowScroll?: boolean }>`
  height: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  background-color: white;
  border: 0.5px solid #8eaeb2;
  box-shadow: 2px 2px 4px 0px rgba(0, 0, 0, 0.2);
  cursor: ${({ draggable }) => (draggable ? 'move' : 'auto')};
  overflow: ${({ allowScroll }) => (allowScroll ? 'auto' : 'visible')};
  overscroll-behavior: ${({ allowScroll }) => (allowScroll ? undefined : 'contain')};
`;

// InnerWrapper
export const slideIn = keyframes`
  from {
    transform: translate(-50%, -55%);
    opacity: 0;
  }
  to {
    transform: translate(-50%, -50%);
    opacity: 1;
  }
`;

interface DimensionProps {
  size?: string;
  dimensions?: Dimensions;
}

const getDimensions =
  (side: 'width' | 'height') =>
    ({ size, dimensions }: DimensionProps): number => {
      const overideDimensions: Record<'width' | 'height', number> = {
        width: parseInt(`${dimensions?.width}`, 10),
        height: parseInt(`${dimensions?.height}`, 10),
      };
      const modalDimension = size ? modalDimensions[size as ModalSize] : undefined;
      const sizeDimensions: Record<'width' | 'height', number> = {
        width: modalDimension ? modalDimension.width : NaN,
        height: modalDimension ? modalDimension.width / modalDimension.aspectRatio : NaN,
      };

      return overideDimensions[side] || sizeDimensions[side];
    };

export const InnerWrapper = styled.div<{
  centerText?: boolean;
  size?: string;
  dimensions?: Dimensions;
}>`
  max-height: 99vh;
  pointer-events: initial;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  outline: 0;
  text-align: ${(props) => props.centerText && 'center'};

  animation-duration: 0.3s;
  animation-iteration-count: 1;
  animation-timing-function: ease;
  animation-name: ${slideIn};

  width: ${getDimensions('width')}px;
  min-height: ${getDimensions('height')}px;
  overflow: auto;
`;

// Close Icon
export const CloseIcon = styled(FjIcon).attrs({
  inline: true,
  viewBox: '0 0 15 15',
  height: 15,
  width: 15,
  alt: 'Remove suffix',
  icon: (
    <Fragment>
      <line x1="2" y1="13" x2="13" y2="2" strokeWidth="2" stroke="#156777" />
      <line x1="2" y1="2" x2="13" y2="13" strokeWidth="2" stroke="#156777" />
    </Fragment>
  ),
})`
  position: absolute;
  right: 20px;
`;

// Header
export const Header = styled.div<{ headerFontSize?: number | string; centerContent?: boolean }>`
  margin-bottom: 20px;
  font-size: ${(props) =>
    props.headerFontSize !== undefined ? evaluateProp(props.headerFontSize) : undefined};
  font-weight: normal;
  text-align: ${(props) => props.centerContent && 'center'};
`;

// Footer
export const Footer = styled.div<{ windows?: boolean }>`
  padding: 15px 20px 15px 20px;
  display: flex;
  justify-content: ${({ windows }) => (windows ? 'flex-start' : 'flex-end')};
  align-items: center;
`;

// Body
export const Body = styled.div<{
  allowScroll?: boolean;
  borderless?: boolean;
  centerContent?: boolean;
}>`
  flex: 1;
  overflow: ${({ allowScroll }) => (allowScroll ? 'auto' : 'visible')};
  padding: ${(props) =>
    (props.borderless && '0 0 0 1px;') || (props.centerContent && '10px 20px 0') || '20px 20px 0'};
  display: flex;
  flex-direction: column;
  justify-content: ${(props) => props.centerContent && 'center'};
`;

export const CancelButton = styled(FjButton).attrs((props: { color?: string }) => ({
  buttonType: fjButtonTypes.LINK,
  defaultStyle: {
    color: props.color,
    border: '10px',
  },
}))``;

export const AcceptButton = styled(FjButton).attrs((props: { color?: string }) => ({
  buttonType: fjButtonTypes.SM,
  defaultStyle: {
    color: props.color,
    border: '10px',
    background: 'white',
  },
}))``;

/* TODO move IMPORT button next to label where it is to do imports.
there is no need for this to be on the footer of the modal.
it disassociates the action from the field it is importing for. */
export const ImportButton = styled(FjButton).attrs((props: { color?: string }) => ({
  buttonType: fjButtonTypes.SM,
  defaultStyle: {
    color: props.color,
    border: '10px',
    background: 'white',
  },
}))``;
